import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { CloudOff, Loader2, RefreshCw, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { usePendingChanges, useOfflineBanner } from './pending-changes'

// Review list for the offline outbox: queued (paused) writes waiting to sync
// and writes whose replay failed. Failed items can be retried or discarded;
// queued items sync automatically on reconnect and can be discarded if the
// user changes their mind.
export function PendingChangesDrawer({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const pending = usePendingChanges()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Pending changes</DialogTitle>
          <DialogDescription>
            Changes made while offline are kept on this device and sync
            automatically when a connection returns. Failed items need
            attention — check the message, then retry or discard.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2">
          {pending.length === 0 && (
            <p className="text-muted-foreground py-4 text-center text-sm">
              Nothing pending — you're all synced.
            </p>
          )}
          {pending.map((item) => (
            <PendingRow key={item.mutation.mutationId} item={item} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function PendingRow({ item }: { item: ReturnType<typeof usePendingChanges>[number] }) {
  const queryClient = useQueryClient()
  const [busy, setBusy] = useState(false)
  const [confirmDiscard, setConfirmDiscard] = useState(false)
  const failed = item.status === 'error'

  const discard = () => {
    queryClient.getMutationCache().remove(item.mutation)
    setConfirmDiscard(false)
  }
  const retry = async () => {
    if (item.mutation.state.status !== 'error') return
    setBusy(true)
    try {
      await item.mutation.execute(item.mutation.state.variables)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex items-start justify-between gap-3 rounded-md border p-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="truncate text-sm font-medium capitalize">{item.label}</span>
          {failed ? (
            <Badge variant="destructive">failed</Badge>
          ) : (
            <Badge variant="outline">waiting to sync</Badge>
          )}
        </div>
        {failed && item.error && (
          <p className="text-destructive mt-1 line-clamp-2 text-xs">{item.error}</p>
        )}
      </div>
      <div className="flex shrink-0 gap-1">
        {failed && (
          <Button size="sm" variant="outline" onClick={retry} disabled={busy}>
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Retry
          </Button>
        )}
        <Button size="sm" variant="ghost" onClick={() => setConfirmDiscard(true)}>
          <Trash2 className="h-4 w-4" />
          Discard
        </Button>
      </div>

      {/* Discard confirmation — discarding permanently drops the queued
          change; it will never sync. */}
      <Dialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Discard this change?</DialogTitle>
            <DialogDescription>
              “{item.label}” is removed from this device and will never sync. This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setConfirmDiscard(false)}>
              Keep It
            </Button>
            <Button size="sm" variant="destructive" onClick={discard}>
              <Trash2 className="mr-1.5 h-4 w-4" />
              Discard
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// Slim status strip for the app shell. Offline = informational (cached data,
// N queued changes); online with failures = actionable (open the drawer).
export function OfflineBanner() {
  const { online, queued, failed } = useOfflineBanner()
  const [drawerOpen, setDrawerOpen] = useState(false)

  if (online && failed === 0 && queued === 0) return null

  const queuedText = `${queued} change${queued === 1 ? '' : 's'} will sync when you're back online`
  const failedText = `${failed} change${failed === 1 ? '' : 's'} couldn't sync`

  return (
    <>
      <button
        type="button"
        onClick={() => setDrawerOpen(true)}
        className={
          online
            ? failed > 0
              ? 'text-destructive flex w-full shrink-0 items-center justify-center gap-2 border-b bg-destructive/10 px-4 py-1.5 text-xs font-medium'
              : 'text-muted-foreground flex w-full shrink-0 items-center justify-center gap-2 border-b px-4 py-1.5 text-xs'
            : 'flex w-full shrink-0 items-center justify-center gap-2 border-b bg-amber-100 px-4 py-1.5 text-xs font-medium text-amber-900 dark:bg-amber-950 dark:text-amber-200'
        }
      >
        {!online && <CloudOff className="h-3.5 w-3.5 shrink-0" />}
        {!online
          ? queued > 0
            ? `Offline — showing cached data · ${queuedText}`
            : 'Offline — showing cached data'
          : failed > 0
            ? failedText
            : `Syncing ${queued} change${queued === 1 ? '' : 's'}…`}
      </button>
      <PendingChangesDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
    </>
  )
}
